import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

/**
 * Saisonale Hero-Sektion mit Auto-Zyklus.
 *
 * Verhalten (analog zu SeasonalCountdown.jsx):
 *  - Wenn `theme` leer, 'standard' oder 'auto' ist  → die aktuelle Saison wird
 *    automatisch anhand des Datums ermittelt (Frühling 20.3., Sommer 21.6.,
 *    Herbst 23.9., Winter 21.12.) und der passende Hero angezeigt.
 *  - Wenn `theme` eine Saison ist (spring/summer/autumn/winter) → wird wie früher
 *    explizit angezeigt.
 *  - Wenn `theme` ein Eintags-Event ist (christmas/easter/mothersday/
 *    valentines/newyear) → das Event hat Vorrang, solange wir innerhalb des
 *    Anzeige-Fensters sind. Außerhalb fällt der Hero zurück auf den
 *    Saison-Auto-Zyklus.
 *
 * Damit muss der Admin nichts mehr manuell auf "spring" oder "summer" stellen –
 * der Shop folgt von selbst dem Jahr.
 */

const SEASONS_CYCLE = [
  { key: 'spring', month: 2, day: 20 },   // 20. März
  { key: 'summer', month: 5, day: 21 },   // 21. Juni
  { key: 'autumn', month: 8, day: 23 },   // 23. September
  { key: 'winter', month: 11, day: 21 },  // 21. Dezember
];

// Wann beginnt/endet ein Eintags-Event-Fenster (in Tagen rund um das Datum)?
const DAY_EVENT_WINDOWS = {
  christmas:  { month: 11, day: 25, daysBefore: 21, daysAfter: 1 },
  newyear:    { month: 0,  day: 1,  daysBefore: 7,  daysAfter: 2 },
  easter:     { month: 3,  day: 20, daysBefore: 14, daysAfter: 1 },
  mothersday: { month: 4,  day: 11, daysBefore: 14, daysAfter: 0 },
  valentines: { month: 1,  day: 14, daysBefore: 10, daysAfter: 0 },
};

// Ermittelt die aktuell laufende Saison anhand des Datums
const resolveCurrentSeason = (now) => {
  const y = now.getFullYear();
  const candidates = [];
  for (let i = -1; i <= 1; i++) {
    SEASONS_CYCLE.forEach((s) => {
      candidates.push({ key: s.key, date: new Date(y + i, s.month, s.day) });
    });
  }
  candidates.sort((a, b) => a.date - b.date);
  let current = null;
  for (const c of candidates) {
    if (c.date <= now) current = c;
    else break;
  }
  return current?.key || 'winter';
};

// Prüft, ob ein explizit gesetztes Eintags-Event aktuell überhaupt noch angezeigt
// werden soll. Außerhalb des Fensters → zurück zum Saison-Auto-Zyklus.
const isDayEventActive = (eventKey, now) => {
  const e = DAY_EVENT_WINDOWS[eventKey];
  if (!e) return false;
  const y = now.getFullYear();
  const eventDate = new Date(y, e.month, e.day);
  const diffDays = Math.floor((now - eventDate) / 86400000);
  // diffDays < 0 → noch vor dem Event (Vorlauf prüfen)
  // diffDays >= 0 → am oder nach dem Event (Nachlauf prüfen)
  if (diffDays < 0) return Math.abs(diffDays) <= e.daysBefore;
  return diffDays <= e.daysAfter;
};

const SeasonalHeroSection = ({ theme, showCollections = false }) => {
  const [now, setNow] = useState(() => new Date());

  // Stundenweise re-evaluieren reicht völlig (Saisonwechsel ist nicht sekundenkritisch)
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // -------------------- Theme-Auflösung --------------------
  // Priorität:
  //   1. Eintags-Event explizit & im Anzeige-Fenster → Event-Theme
  //   2. Saison explizit gesetzt → Saison-Theme
  //   3. Sonst (null, '', 'standard', 'auto', unbekannt) → Auto-Saison
  let resolvedTheme = null;

  if (theme && DAY_EVENT_WINDOWS[theme] && isDayEventActive(theme, now)) {
    resolvedTheme = theme;
  } else if (theme && ['spring', 'summer', 'autumn', 'winter'].includes(theme)) {
    resolvedTheme = theme;
  } else {
    resolvedTheme = resolveCurrentSeason(now);
  }

  const themeContent = {
    christmas: {
      title: '🎄 Weihnachtszauber bei Homeyoursoul',
      subtitle: 'Entdecken Sie unsere festliche Kollektion für besinnliche Momente',
      description: 'Von stimmungsvollen Dekorationen bis zu herzerwärmenden Geschenken - finden Sie alles für ein unvergessliches Weihnachtsfest',
      buttonText: 'Zur Weihnachtskollektion',
      gradient: 'from-red-700 via-red-600 to-green-700',
      accentColor: '#C41E3A',
      secondaryColor: '#0F5132',
      decorations: ['❄️', '🎄', '⭐', '🎁', '☃️', '🔔'],
      image: '🎅'
    },
    newyear: {
      title: `🎉 Frohes Neues Jahr ${now.getFullYear()}!`,
      subtitle: 'Starten Sie mit frischer Energie ins neue Jahr',
      description: 'Neue Vorsätze, neue Möglichkeiten – entdecken Sie unsere Neujahrskollektion und starten Sie perfekt ins neue Jahr',
      buttonText: 'Neujahrsangebote entdecken',
      gradient: 'from-blue-600 via-purple-600 to-pink-600',
      accentColor: '#FFD700',
      secondaryColor: '#4A90E2',
      decorations: ['🎆', '🎊', '✨', '🥂', '🎉', '🌟'],
      image: '🎆'
    },
    easter: {
      title: '🐰 Frühlingsgefühle & Osterzauber',
      subtitle: 'Begrüßen Sie den Frühling mit unserer bunten Osterkollektion',
      description: 'Fröhliche Farben, frühlingsfrische Designs und liebevolle Geschenkideen für Familie und Freunde',
      buttonText: 'Zur Osterkollektion',
      gradient: 'from-pink-400 via-purple-400 to-pink-500',
      accentColor: '#FFB6C1',
      secondaryColor: '#98D8C8',
      decorations: ['🐰', '🌸', '🥚', '🌷', '🦋', '🌼'],
      image: '🐣'
    },
    mothersday: {
      title: '💐 Für die beste Mama der Welt',
      subtitle: 'Zeigen Sie Ihre Liebe mit besonderen Geschenken',
      description: 'Liebevoll ausgewählte Produkte, die von Herzen kommen – weil Mama nur das Beste verdient',
      buttonText: 'Geschenke für Mama entdecken',
      gradient: 'from-pink-500 via-rose-500 to-pink-600',
      accentColor: '#FF69B4',
      secondaryColor: '#F472B6',
      decorations: ['💐', '🌸', '🌺', '💝', '🦋', '🌷'],
      image: '👩'
    },
    valentines: {
      title: '💕 Liebe liegt in der Luft',
      subtitle: 'Romantische Geschenke für Ihre Liebsten',
      description: 'Von herzlichen Überraschungen bis zu romantischen Momenten – feiern Sie die Liebe mit unserer exklusiven Auswahl',
      buttonText: 'Romantische Geschenke entdecken',
      gradient: 'from-red-500 via-pink-500 to-red-600',
      accentColor: '#FF1493',
      secondaryColor: '#FFB6C1',
      decorations: ['💕', '💝', '💖', '🌹', '💐', '❤️'],
      image: '💑'
    },
    spring: {
      title: '🌷 Frühlingserwachen bei Homeyoursoul',
      subtitle: 'Frische Farben & zarte Akzente für ein lebendiges Zuhause',
      description: 'Leichte Stoffe, blühende Motive und Geschenke, die den Frühling nach Hause holen – lassen Sie sich von der neuen Saison inspirieren.',
      buttonText: 'Zur Frühlingskollektion',
      gradient: 'from-pink-300 via-rose-300 to-yellow-300',
      accentColor: '#E89BAE',
      secondaryColor: '#A8D5BA',
      decorations: ['🌷', '🌸', '🌼', '🦋', '🌿', '🐦'],
      image: '🌷'
    },
    summer: {
      title: '☀️ Sommer, Sonne, Urlaubsfeeling',
      subtitle: 'Perfekte Begleiter für sonnige Tage',
      description: 'Leicht, luftig, farbenfroh – unsere Sommerkollektion bringt Urlaubsstimmung in Ihr Zuhause',
      buttonText: 'Zur Sommerkollektion',
      gradient: 'from-orange-100 via-rose-100 to-amber-100',
      accentColor: '#C4704A',
      secondaryColor: '#5D4E3C',
      decorations: ['☀️', '🌊', '🏖️', '🍍', '🥥', '🌺', '🦜', '🍉'],
      image: '🏝️',
      light: true
    },
    autumn: {
      title: '🍂 Herbstliche Gemütlichkeit',
      subtitle: 'Kuschelige Momente für kühle Tage',
      description: 'Warme Farben, gemütliche Atmosphäre – holen Sie sich den goldenen Herbst nach Hause',
      buttonText: 'Zur Herbstkollektion',
      gradient: 'from-[#F7E6D6] via-[#F0D3BC] to-[#E8C2A4]',
      accentColor: '#B5643C',
      secondaryColor: '#5D4E3C',
      decorations: ['🍂', '🍁', '🌰', '🍄'],
      image: null,
      light: true
    },
    winter: {
      title: '❄️ Winterzauber bei Homeyoursoul',
      subtitle: 'Kuschelige Wärme & glitzernde Akzente für kalte Tage',
      description: 'Sanfte Töne, weiche Materialien und winterliche Lieblingsstücke – machen Sie Ihr Zuhause zum gemütlichsten Ort der Saison.',
      buttonText: 'Zur Winterkollektion',
      gradient: 'from-blue-400 via-indigo-500 to-slate-600',
      accentColor: '#7AB6E0',
      secondaryColor: '#4F6D8A',
      decorations: ['❄️', '⛄', '🌨️', '🕯️', '🧣', '✨'],
      image: '❄️'
    }
  };

  const content = themeContent[resolvedTheme];

  const isLight = content?.light === true;
  const textPrimary = isLight ? '#5D4E3C' : '#FFFFFF';   // Brand-Braun auf hellen Themes, sonst Weiß
  const textSecondary = isLight ? 'rgba(93,78,61,0.85)' : 'rgba(255,255,255,0.95)';
  const btnPrimaryBg = isLight ? '#5D4E3C' : '#FFFFFF';
  const btnPrimaryText = isLight ? '#F5EDE4' : (content?.accentColor || '#5D4E3C');
  const btnOutlineColor = isLight ? '#5D4E3C' : '#FFFFFF';
  const benefitBoxBg = isLight ? 'bg-white/60 hover:bg-white/80' : 'bg-white/20 hover:bg-white/30';

  // Hintergrund-Deko: gleichmäßige 5x4-Gitter-Verteilung mit sanftem Jitter — genug Atmosphäre,
  // aber keine Icons direkt hinter dem Hauptmotiv (Palme etc.).
  const bgDecorations = useMemo(() => {
    const cols = 5;
    const rows = 4;
    const items = [];
    const rand = () => Math.random();
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cellW = 100 / cols;
        const cellH = 100 / rows;
        const left = c * cellW + cellW * (0.2 + rand() * 0.6);
        const top = r * cellH + cellH * (0.2 + rand() * 0.6);
        const rotate = (rand() * 30 - 15).toFixed(1);
        const delay = (rand() * 3).toFixed(2);
        const duration = (5 + rand() * 4).toFixed(2);
        // Kleine Sicherheitszone direkt um das Hauptmotiv (Palme in der rechten Bildhälfte auf Desktop)
        const inHeroZone = left > 62 && left < 88 && top > 30 && top < 75;
        if (inHeroZone) continue;
        items.push({ left, top, rotate, delay, duration });
      }
    }
    return items;
  }, [resolvedTheme]);

  if (!content) return null;

  return (
    <div
      data-testid={`seasonal-hero-${resolvedTheme}`}
      className={`relative w-full overflow-hidden bg-gradient-to-br ${content.gradient} py-8 md:py-14`}
    >
      {/* Animated background decorations — gleichmäßig verteilt, Sicherheitszone um das Hauptmotiv */}
      <div className={`absolute inset-0 overflow-hidden pointer-events-none hidden md:block ${isLight ? 'opacity-50' : 'opacity-25'}`}>
        {bgDecorations.map((d, i) => (
          <div
            key={i}
            className="absolute text-4xl md:text-6xl animate-float"
            style={{
              left: `${d.left}%`,
              top: `${d.top}%`,
              transform: `translate(-50%, -50%) rotate(${d.rotate}deg)`,
              animationDelay: `${d.delay}s`,
              animationDuration: `${d.duration}s`,
            }}
          >
            {content.decorations[i % content.decorations.length]}
          </div>
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-4 md:gap-8 items-center">
          {/* Text Content */}
          <div className="space-y-3 md:space-y-4" style={{ color: textPrimary }}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              {content.title}
            </h1>
            <p className="text-xl md:text-2xl font-semibold" style={{ color: textSecondary }}>
              {content.subtitle}
            </p>
            <p className="text-base md:text-lg leading-relaxed" style={{ color: textSecondary }}>
              {content.description}
            </p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Link to="/products">
                <button
                  data-testid="seasonal-hero-cta-products"
                  className="px-8 py-4 text-lg font-semibold rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
                  style={{ backgroundColor: btnPrimaryBg, color: btnPrimaryText }}
                >
                  {content.buttonText}
                </button>
              </Link>
              <Link to="/categories">
                <button
                  data-testid="seasonal-hero-cta-categories"
                  className="px-8 py-4 border-2 text-lg font-semibold rounded-lg hover:bg-white/20 transition-all duration-300"
                  style={{ borderColor: btnOutlineColor, color: btnOutlineColor }}
                >
                  Alle Kategorien
                </button>
              </Link>
            </div>
          </div>

          {/* Visual Element */}
          {content.image && (
          <div className="flex items-center justify-center">
            <div className="relative w-full flex items-center justify-center min-h-[120px] md:min-h-[240px]">
              {/* Main Icon/Image — steht frei, keine Deko-Icons direkt hinter der Palme */}
              <div
                className="text-7xl sm:text-8xl md:text-[10rem] animate-bounce relative z-10"
                style={{
                  filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.25))',
                  animationDuration: '3s'
                }}
              >
                {content.image}
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Vorteils-Boxen — wahrheitsbezogene Kunden-Vorteile */}
        <div className={`mt-6 md:mt-10 grid grid-cols-2 ${showCollections ? 'md:grid-cols-4' : 'md:grid-cols-2 max-w-2xl mx-auto'} gap-3 text-center`} style={{ color: textPrimary }}>
          {showCollections && (<>
          <Link
            to="/collections"
            data-testid="hero-benefit-themen-boxen"
            className={`${benefitBoxBg} backdrop-blur-sm rounded-lg p-4 transition-all duration-200`}
          >
            <div className="text-3xl font-bold leading-none">🎁</div>
            <div className="text-sm font-semibold mt-1">Themen-Boxen</div>
            <div className="text-[11px] opacity-90 mt-0.5">Kuratiert pro Stimmung</div>
          </Link>
          <Link
            to="/collections"
            data-testid="hero-benefit-limited-collections"
            className={`${benefitBoxBg} backdrop-blur-sm rounded-lg p-4 transition-all duration-200`}
          >
            <div className="text-3xl font-bold leading-none">✨</div>
            <div className="text-sm font-semibold mt-1">Limited Collections</div>
            <div className="text-[11px] opacity-90 mt-0.5">Saisonal & exklusiv</div>
          </Link>
          </>)}
          <Link
            to="/products?surprise=1"
            data-testid="hero-benefit-ueberraschung"
            className={`${benefitBoxBg} backdrop-blur-sm rounded-lg p-4 transition-all duration-200`}
          >
            <div className="text-3xl font-bold leading-none">🎉</div>
            <div className="text-sm font-semibold mt-1">Deine Überraschung</div>
            <div className="text-[11px] opacity-90 mt-0.5">Lass dich überraschen</div>
          </Link>
          <Link
            to="/products"
            data-testid="hero-benefit-fuer-dich-sie-ihn"
            className={`${benefitBoxBg} backdrop-blur-sm rounded-lg p-4 transition-all duration-200`}
          >
            <div className="text-3xl font-bold leading-none">💝</div>
            <div className="text-sm font-semibold mt-1">Für dich, sie & ihn</div>
            <div className="text-[11px] opacity-90 mt-0.5">Verschenken oder selbst genießen</div>
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
  );
};

export default SeasonalHeroSection;
